import { request } from "../http/api-client";

const BASE = (typeof import.meta !== "undefined" && (import.meta as any).env?.VITE_PAYOUTS_BASE_URL) || "http://localhost:3099";

export interface PayoutApproval {
  id: string;
  merchantId: string;
  beneficiaryId: string;
  amount: number;
  currency: string;
  fees: number;
  tax: number;
  netAmount: number;
  status: string;
  method: string;
  mode: string;
  purpose: string;
  reference?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  merchantName?: string;
  beneficiaryDetails?: {
    name: string;
    // Crypto fields
    walletAddress?: string;
    network?: string;
    // Bank fields
    accountNumber?: string;
    ifscCode?: string;
    bankName?: string;
  };
  beneficiary: {
    id: string;
    merchantId: string;
    name: string;
    email?: string;
    phone?: string;
    address?: string;
    isActive: boolean;
    totalPayouts: number;
    totalAmount: number;
    createdAt: string;
    updatedAt: string;
  };
  merchant?: {
    id: string;
    name: string;
  };
}

export interface PayoutStats {
  pending: number;
  processing: number;
  success: number;
  failed: number;
  cancelled: number;
  total: number;
  totalAmount: number;
  today: {
    count: number;
    amount: number;
  };
}

export interface PayoutHistoryEntry {
  id: string;
  action: string;
  userId: string;
  details: any;
  createdAt: string;
}

export type PayoutStatus =
  | "PENDING"
  | "QUEUED"
  | "PROCESSING"
  | "SUCCESS"
  | "FAILED"
  | "CANCELLED";

export interface UpdatePayoutRequest {
  status?: PayoutStatus;
  notes?: string;
  metadata?: Record<string, any>;
  scheduledAt?: string;
}

export interface PendingPayoutsFilters {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  status?: string;
  amountFrom?: number;
  amountTo?: number;
  dateFrom?: string;
  dateTo?: string;
  currency?: string;
  method?: string;
  processingType?: string;
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

/**
 * Fetch all pending payouts across all merchants (Admin only)
 */
export async function fetchPendingPayouts(filters: PendingPayoutsFilters = {}) {
  const params = new URLSearchParams();

  if (filters.page) params.set("page", String(filters.page));
  if (filters.limit) params.set("limit", String(filters.limit));
  if (filters.search) params.set("search", filters.search);
  if (filters.sortBy) params.set("sortBy", filters.sortBy);
  if (filters.sortOrder) params.set("sortOrder", filters.sortOrder);
  if (filters.status) params.set("status", filters.status);
  if (filters.amountFrom) params.set("amountFrom", String(filters.amountFrom));
  if (filters.amountTo) params.set("amountTo", String(filters.amountTo));
  if (filters.dateFrom) params.set("dateFrom", filters.dateFrom);
  if (filters.dateTo) params.set("dateTo", filters.dateTo);
  if (filters.currency) params.set("currency", filters.currency);
  if (filters.method) params.set("method", filters.method);
  if (filters.processingType) params.set("processingType", filters.processingType);

  const url = `${BASE}/api/v1/payouts/admin/pending${params.toString() ? `?${params.toString()}` : ""}`;
  const res = await request<{
    success: boolean;
    data: PayoutApproval[];
    pagination: PaginationInfo;
    message: string;
  }>(url, { method: "GET" });

  return res;
}

/**
 * Fetch admin payout statistics
 */
export async function fetchPayoutStats() {
  const url = `${BASE}/api/v1/payouts/admin/stats`;
  const res = await request<{
    success: boolean;
    data: PayoutStats;
    message: string;
  }>(url, { method: "GET" });

  return res.data;
}

/**
 * Approve a single payout
 */
export async function approvePayout(payoutId: string) {
  const url = `${BASE}/api/v1/payouts/admin/${payoutId}/approve`;
  const res = await request<{
    success: boolean;
    data: PayoutApproval;
    message: string;
  }>(url, { method: "POST" });

  return res;
}

/**
 * Reject a single payout with reason
 */
export async function rejectPayout(payoutId: string, reason: string) {
  const url = `${BASE}/api/v1/payouts/admin/${payoutId}/reject`;
  const res = await request<{
    success: boolean;
    data: PayoutApproval;
    message: string;
  }>(url, {
    method: "POST",
    body: { reason },
  });

  return res;
}

/**
 * Bulk approve multiple payouts
 */
export async function bulkApprovePayouts(payoutIds: string[]) {
  const url = `${BASE}/api/v1/payouts/admin/bulk-approve`;
  const res = await request<{
    success: boolean;
    data: {
      approved: number;
      failed: number;
      total: number;
      errors?: { payoutId: string; error: string }[];
    };
    message: string;
  }>(url, {
    method: "POST",
    body: { payoutIds },
  });

  return res;
}

/**
 * Fetch payout history/audit trail
 */
export async function fetchPayoutHistory(payoutId: string) {
  const url = `${BASE}/api/v1/payouts/${payoutId}/history`;
  const res = await request<{
    success: boolean;
    data: PayoutHistoryEntry[];
    message: string;
  }>(url, { method: "GET" });

  return res.data;
}

/**
 * Update payout details (Admin only)
 * @param payoutId - ID of the payout to update
 * @param data - Fields to update (status, notes, metadata, scheduledAt)
 */
export async function updatePayout(
  payoutId: string,
  data: UpdatePayoutRequest
) {
  const url = `${BASE}/api/v1/payouts/${payoutId}`;
  const res = await request<{
    success: boolean;
    data: PayoutApproval;
    message: string;
  }>(url, {
    method: "PUT",
    body: data,
  });

  return res.data;
}
